import { useCallback, useEffect, useRef, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import * as Sentry from '@sentry/react';
import { log } from '../utils/logger';
import {
  RICONTROLLO_RAPIDO_MS,
  leggiVersioneServer,
  percorsoWorkerBloccato,
  recuperaScavalcandoIlPrecache,
} from '../utils/controllo-versione';

const SW_UPDATE_MS = 30 * 60 * 1000; // ask the browser for a new worker every 30 min
const CHECK_THROTTLE_MS = 10 * 60 * 1000; // …but never more than once per 10 min
const IDLE_MS = 45 * 1000; // "not touching anything" threshold before a silent reload
const READY_POLL_MS = 5 * 1000; // how often to re-test the idle/hidden condition
/** Spread simultaneous reloads so every tab doesn't hit /auth/refresh at once. */
const RELOAD_JITTER_MS = 3000;
/** Don't re-apply straight after applying — guards against an activation loop. */
const REAPPLY_COOLDOWN_MS = 5 * 60 * 1000;
/** …and give up entirely if a tab somehow keeps applying. */
const MAX_APPLIES_PER_TAB = 3;
const APPLY_STATE_KEY = 'draughts-sw-apply-state';
/** Il recupero distruttivo si fa una volta sola per sessione — vedi `recuperaOra`. */
const RECOVERY_STATE_KEY = 'draughts-sw-recovery';
/** Quanti `update()` falliti di fila prima di dirlo a Sentry. */
const FALLIMENTI_PRIMA_DI_SEGNALARE = 3;

interface ApplyState {
  at: number;
  n: number;
}

function readApplyState(): ApplyState | null {
  try {
    const raw = sessionStorage.getItem(APPLY_STATE_KEY);
    return raw ? (JSON.parse(raw) as ApplyState) : null;
  } catch {
    return null;
  }
}

/** Il recupero è già stato tentato in questa sessione? */
function recuperoGiaFatto(): boolean {
  try {
    return sessionStorage.getItem(RECOVERY_STATE_KEY) !== null;
  } catch {
    /* private mode / storage pieno: meglio non ritentare che ritentare all'infinito */
    return true;
  }
}

/**
 * Silently moves the app onto a freshly deployed build.
 *
 * `registerType: 'prompt'` (see vite.config.ts) keeps a new service worker in the
 * *waiting* state so it can never reload the page on its own — but that also means
 * it would only activate on a clean restart, which an installed PWA that is merely
 * backgrounded may not get for weeks. This hook closes that gap: it activates the
 * waiting worker itself, at a moment when a reload costs nothing.
 *
 * The caller owns the "costs nothing" decision and passes it as `safeToReload`
 * (see isSafeToReload); on top of it this hook requires the user to be idle or the
 * tab to be hidden, which covers the modals App cannot see into.
 *
 * ## Why this hook, not the plugin, performs the reload
 *
 * In 'prompt' mode `updateServiceWorker()` only posts SKIP_WAITING (its `reloadPage`
 * argument is ignored). vite-plugin-pwa normally reloads from a `controlling`
 * listener, which is wrong for us on two counts:
 *
 *  - it fires `if (event.isUpdate)`, and workbox sets `isUpdate` from whether the
 *    page had a controller *at registration time*. After a hard reload it is false,
 *    so nobody would reload — leaving the page running old JS while the activated
 *    worker has already dropped the caches its lazy chunks live in;
 *  - skipWaiting is global. With `clientsClaim` the new worker takes over *every*
 *    open tab, so a tab in the middle of a game would be reloaded by a decision
 *    another tab made. Passing `onNeedReload` takes that away from the plugin, and
 *    each tab then reloads only once *it* is safe.
 *
 * ## La rete di sicurezza
 *
 * Tutto quanto sopra passa da un solo posto: la ri-lettura di `/sw.js`. Dove quella
 * richiesta non può riuscire — un host che mette la parte statica dietro basic
 * auth, dove il service worker non ha modo di autenticarsi — nessun worker arriva
 * mai in attesa e l'app resta sul bundle vecchio per sempre, in silenzio. La sonda
 * su `/api/version` è ciò che se ne accorge lo stesso; vedi `controllo-versione.ts`.
 */
export function useAppUpdate(safeToReload: boolean): void {
  const registrationRef = useRef<ServiceWorkerRegistration | undefined>(undefined);
  const lastActivityRef = useRef<number>(Date.now());
  /*
   * Zero, non `Date.now()`: nascendo a "adesso", il PRIMO controllo possibile
   * cadeva dieci minuti dopo l'apertura dell'app — e in una PWA che si apre e si
   * chiude quei dieci minuti erano spesso tutta la sessione. Ora il controllo
   * d'avvio passa subito, e il throttle vale solo da li' in poi.
   */
  const lastCheckRef = useRef<number>(0);
  const appliedRef = useRef(false);
  const reloadingRef = useRef(false);
  /** A controlled page whose worker changes is running stale assets. */
  const hadControllerRef = useRef(false);
  /** Letture consecutive di `/api/version` che hanno trovato una versione diversa. */
  const mismatchRef = useRef(0);
  /** `update()` falliti di fila, per non segnalare una rete che balla. */
  const fallimentiRef = useRef(0);
  const segnalatoRef = useRef(false);
  const recuperoRef = useRef(false);
  /*
   * `needRefresh` letto da una ref e non dalle dipendenze: `sondaVersione` finisce
   * dentro `checkForNewWorker`, che e' la dipendenza dell'intervallo da 30 minuti.
   * Prenderlo per dipendenza rifonderebbe quell'intervallo a ogni cambio di stato,
   * rimettendone a zero il conto alla rovescia.
   */
  const needRefreshRef = useRef(false);
  /** Un `update()` in volo: finche' non risponde non si sa niente. */
  const updateInCorsoRef = useRef(false);
  /** Il ricontrollo ravvicinato programmato dopo la prima lettura disallineata. */
  const ricontrolloRef = useRef<number | undefined>(undefined);
  /*
   * `checkForNewWorker` letto da una ref: la sonda deve poter programmare il
   * proprio ricontrollo, ma e' `checkForNewWorker` a chiamare la sonda — senza
   * questo rimando le due si definirebbero a vicenda.
   */
  const checkRef = useRef<(reason: string) => void>(() => {});

  // Set once the new worker has taken over and this tab must move onto it.
  const [reloadPending, setReloadPending] = useState(false);
  // Set once la sonda ha stabilito che il percorso service worker è bloccato.
  const [recuperoNecessario, setRecuperoNecessario] = useState(false);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      registrationRef.current = registration;
    },
    onRegisterError(error) {
      log.warn('AppUpdate', 'Service worker registration failed', error);
    },
    // Replaces the plugin's own `window.location.reload()` — see the note above.
    onNeedReload() {
      setReloadPending(true);
    },
  });

  useEffect(() => {
    needRefreshRef.current = needRefresh;
  }, [needRefresh]);

  /**
   * Chiede al server che versione serve, e conta le volte di fila che non
   * coincide con questo bundle. Solo contabilità: la decisione la prende il
   * `tick`, che è l'unico posto che sa anche se il momento è sicuro.
   */
  const sondaVersione = useCallback(() => {
    if (recuperoGiaFatto()) return;
    void leggiVersioneServer().then((serverVersion) => {
      if (serverVersion === null || serverVersion === __APP_VERSION__) {
        mismatchRef.current = 0;
        return;
      }
      mismatchRef.current += 1;
      log.warn('AppUpdate', 'il server serve una versione diversa', {
        app: __APP_VERSION__,
        server: serverVersion,
        letture: mismatchRef.current,
      });
      const registrazione = registrationRef.current;
      // Si rileggono QUI e non alla decisione: se nel frattempo un worker arriva,
      // il `tick` lo rivede comunque un istante prima di agire.
      const inArrivo =
        needRefreshRef.current || Boolean(registrazione?.waiting) || Boolean(registrazione?.installing);
      if (
        percorsoWorkerBloccato({
          appVersion: __APP_VERSION__,
          serverVersion,
          mismatchConsecutivi: mismatchRef.current,
          workerInArrivo: inArrivo,
          updateInCorso: updateInCorsoRef.current,
        })
      ) {
        setRecuperoNecessario(true);
        return;
      }
      // La lettura successiva non deve aspettare il throttle ordinario: senza
      // questo il ripiego arrivava dopo venti minuti buoni, e fino a un'ora se
      // l'app restava ferma in primo piano (li' l'unico trigger e' l'intervallo
      // da 30'). Ma solo finche' il percorso normale non da' segni di vita: se un
      // worker sta arrivando il disallineamento si risolve da se', e continuare a
      // sondare sarebbe una richiesta al minuto per niente.
      if (!inArrivo) {
        window.clearTimeout(ricontrolloRef.current);
        ricontrolloRef.current = window.setTimeout(
          () => checkRef.current('ricontrollo'),
          RICONTROLLO_RAPIDO_MS
        );
      }
    });
  }, []);

  const checkForNewWorker = useCallback(
    (reason: string) => {
      if (!navigator.onLine) return;
      const now = Date.now();
      // Con una lettura gia' disallineata alle spalle si ricontrolla presto: e'
      // il ricontrollo programmato dalla sonda, e il throttle ordinario lo
      // scarterebbe.
      const attesaMinima = mismatchRef.current > 0 ? RICONTROLLO_RAPIDO_MS : CHECK_THROTTLE_MS;
      if (now - lastCheckRef.current < attesaMinima) return;
      lastCheckRef.current = now;
      const inVolo = registrationRef.current?.update();
      updateInCorsoRef.current = inVolo !== undefined;
      inVolo
        ?.then(() => {
          updateInCorsoRef.current = false;
          fallimentiRef.current = 0;
        })
        .catch((e) => {
          updateInCorsoRef.current = false;
          /* offline / transient — retried on the next trigger */
          log.warn('AppUpdate', 'SW update() failed', { reason, error: (e as Error)?.message });
          fallimentiRef.current += 1;
          // Un `update()` fallito ogni tanto è la rete; tre di fila è l'host.
          // Senza questa riga il guasto è MUTO: logger.ts spegne log.warn nelle
          // build di produzione, che è come è riuscito a durare un mese.
          if (fallimentiRef.current >= FALLIMENTI_PRIMA_DI_SEGNALARE && !segnalatoRef.current) {
            segnalatoRef.current = true;
            Sentry.captureMessage('SW update failed', {
              level: 'warning',
              tags: { swUpdateFallito: 'true' },
              extra: { appVersion: __APP_VERSION__, reason, error: (e as Error)?.message },
            });
          }
        });
      // Sugli stessi trigger e sotto lo stesso throttle: la sonda serve proprio a
      // scoprire che la riga qui sopra non sta arrivando da nessuna parte.
      sondaVersione();
    },
    [sondaVersione]
  );

  useEffect(() => {
    checkRef.current = checkForNewWorker;
  }, [checkForNewWorker]);

  // Look for a freshly deployed worker while the app stays open, so one is already
  // waiting by the time a safe moment comes around.
  useEffect(() => {
    const onFocus = () => checkForNewWorker('focus');
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      // Coming back to the app is not the moment to reload in someone's face:
      // timers were frozen while hidden, so without this the tab would look idle
      // for however long it was away.
      lastActivityRef.current = Date.now();
      checkForNewWorker('visible');
    };

    // Subito, all'apertura: e' il momento in cui e' piu' probabile che il server
    // abbia una build piu' nuova di quella che sta partendo dal precache.
    checkForNewWorker('avvio');

    const interval = window.setInterval(() => checkForNewWorker('interval'), SW_UPDATE_MS);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(ricontrolloRef.current);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [checkForNewWorker]);

  // The moment this tab becomes idle enough to swap builds is also the moment it is
  // worth asking whether there is a new one, instead of waiting out the interval.
  useEffect(() => {
    if (safeToReload) checkForNewWorker('became-safe');
  }, [safeToReload, checkForNewWorker]);

  // Track "is the user in the middle of something?". A modal owned by a lazy screen
  // is invisible to the predicate, but someone using one is never idle. Scrolling
  // counts: reading the dashboard with a wheel produces no pointer or key events.
  useEffect(() => {
    const touch = () => {
      lastActivityRef.current = Date.now();
    };
    const events = ['pointerdown', 'keydown', 'wheel', 'touchmove', 'scroll'] as const;
    for (const event of events) window.addEventListener(event, touch, { passive: true });
    /*
     * `document.body` per lo `scroll`: il contenitore di scorrimento di questa app
     * è `<body>`, e l'evento `scroll` di un elemento ORDINARIO non risale a
     * `window`. `wheel` e `touchmove` coprono già il caso comune, quindi il
     * residuo era stretto — trascinare la barra di scorrimento col mouse su una
     * pagina Regole per quarantacinque secondi — ma è un'attività reale che il
     * predicato leggeva come inattività.
     */
    document.body.addEventListener('scroll', touch, { passive: true });
    return () => {
      for (const event of events) window.removeEventListener(event, touch);
      document.body.removeEventListener('scroll', touch);
    };
  }, []);

  // Catches the case the plugin's own listener misses: a page that was not
  // controlled at registration (a hard reload) still needs to move onto the worker
  // it just activated. Ignored on a first visit, where `clientsClaim` fires this
  // for the very first worker and there is nothing stale to escape.
  useEffect(() => {
    hadControllerRef.current = Boolean(navigator.serviceWorker?.controller);
    const onControllerChange = () => {
      if (hadControllerRef.current || appliedRef.current) setReloadPending(true);
    };
    navigator.serviceWorker?.addEventListener('controllerchange', onControllerChange);
    return () => navigator.serviceWorker?.removeEventListener('controllerchange', onControllerChange);
  }, []);

  // Activate the waiting worker, then move this tab onto it — both gated on the
  // same safe moment. Polled rather than purely reactive because "45s have passed
  // with no input" is not an event React re-renders on.
  useEffect(() => {
    if (!safeToReload) return;
    if (!needRefresh && !reloadPending && !recuperoNecessario) return;

    const coastIsClear = () =>
      Date.now() - lastActivityRef.current >= IDLE_MS || document.visibilityState === 'hidden';

    const reloadNow = () => {
      if (reloadingRef.current) return;
      reloadingRef.current = true;
      // Jitter: every tab is claimed at the same instant, and a synchronised stampede
      // onto the single-use refresh token would log the losers out.
      window.setTimeout(() => window.location.reload(), Math.floor(Math.random() * RELOAD_JITTER_MS));
    };

    /**
     * Il ripiego, quando un worker nuovo non arriva e non arriverà: si butta via
     * il precache e si ricarica in rete. Una volta sola per sessione, perché
     * `__APP_VERSION__` viene dalla package.json di radice mentre `/api/version`
     * viene da quella del server: i sei file del bump si allineano a mano e hanno
     * già divergato una volta. Un disallineamento permanente non deve poter
     * innescare un ciclo di ricarichi.
     */
    const recuperaOra = () => {
      if (recuperoRef.current || recuperoGiaFatto()) return;
      recuperoRef.current = true;
      try {
        sessionStorage.setItem(RECOVERY_STATE_KEY, String(Date.now()));
      } catch {
        /* private mode / storage pieno: il freno è già dato da recuperoRef */
      }
      log.warn('AppUpdate', 'percorso service worker bloccato: recupero scavalcando il precache', {
        from: __APP_VERSION__,
      });
      void recuperaScavalcandoIlPrecache().then(reloadNow, reloadNow);
    };

    const tick = () => {
      if (!coastIsClear()) return;

      if (reloadPending) {
        log.info('AppUpdate', 'New worker in control, reloading onto it', { from: __APP_VERSION__ });
        reloadNow();
        return;
      }

      if (!needRefresh) {
        // Ripensamento dell'ultimo istante: se nel frattempo un worker è arrivato,
        // il percorso normale ha la precedenza e non si butta via niente.
        const registrazione = registrationRef.current;
        const inArrivo = Boolean(registrazione?.waiting) || Boolean(registrazione?.installing);
        if (recuperoNecessario && !inArrivo && !updateInCorsoRef.current) recuperaOra();
        return;
      }

      if (appliedRef.current) return;

      const state = readApplyState();
      const now = Date.now();
      // A worker that installs but never manages to take control would otherwise
      // have every page load re-trigger the update.
      if (state && now - state.at < REAPPLY_COOLDOWN_MS) return;
      const n = (state?.n ?? 0) + 1;
      if (n > MAX_APPLIES_PER_TAB) {
        appliedRef.current = true;
        log.error('AppUpdate', 'Giving up on auto-update: applied too many times in this tab', { n });
        return;
      }

      appliedRef.current = true;
      try {
        sessionStorage.setItem(APPLY_STATE_KEY, JSON.stringify({ at: now, n } satisfies ApplyState));
      } catch {
        /* private mode / storage full — the cooldown is a nicety, not a requirement */
      }
      log.info('AppUpdate', 'Activating the waiting worker', { from: __APP_VERSION__, attempt: n });
      // Posts SKIP_WAITING; the reload follows from onNeedReload/controllerchange.
      updateServiceWorker().catch((e) => {
        log.warn('AppUpdate', 'updateServiceWorker failed, hard reloading', { error: (e as Error)?.message });
        reloadNow();
      });
    };

    tick();
    const interval = window.setInterval(tick, READY_POLL_MS);
    return () => window.clearInterval(interval);
  }, [needRefresh, reloadPending, recuperoNecessario, safeToReload, updateServiceWorker]);
}
